// EIP-1193 provider events, treated as session-fatal.
//
// ## Why this file exists at all
//
// Before Phase 1 this SPA subscribed to NOTHING. `accountsChanged` and
// `chainChanged` appeared nowhere in the source or the built bundle, chainId was
// read once at connect, and the header rendered the login-time address out of
// sessionStorage forever. A mid-session wallet switch was therefore invisible:
// the console kept presenting full authority under the old signer's name.
//
// That was a presentation defect while the session was bound to a database row.
// It is not one now. A Phase 1 session asserts "this signer is CURRENTLY an owner
// of the canonical vault", so switching to an account that is NOT an owner leaves
// the console showing an authority that belongs to somebody else.
//
// ─── The chain a wallet happens to be on is NOT identity ──────────────────
//
// `chainChanged` is deliberately NOT a session event, and this file used to have
// it backwards. The canonical authority lives server-side on chain 84532, and
// the server re-reads membership there no matter what network the browser's
// wallet is pointed at. So the connected chain is a property of the WALLET, not
// of the operator, the signer, or the vault — and ending a session because
// someone glanced at Ethereum mainnet would be logging a person out over a
// setting that has no bearing on who they are.
//
// The wallet's chain still matters at one moment: signing. MetaMask refuses
// `eth_signTypedData_v4` when `domain.chainId` does not match the connected
// network, so every signing flow — the SIWE login and the approval — binds the
// chain the wallet reports at that moment, whichever EVM chain it is. Nothing
// asks the wallet to switch, and nothing holds the session hostage to a network.
//
// ## A client listener is never the control
//
// Anyone can disable JavaScript. The server re-checks live vault membership on
// authority-sensitive calls, and THAT is the control. This exists so the console
// stops *presenting* an authority it may no longer have, and so the server-side
// session is ended promptly rather than lingering until it expires.
//
// ## Why invalidation is a latch
//
// "Switch away and switch back" must not restore the old session. Ending it
// server-side already guarantees that, but the latch makes the intent explicit
// and survives a race where the logout call is still in flight: once a reason is
// set, nothing clears it except a fresh, explicit login.

import { clearSession, getSessionMetadata, getSessionToken } from "./session";

/**
 * Why the browser session was ended. Null when no invalidation has happened.
 *
 * There is no `chain_changed`. Switching networks does not end a session — see
 * the chain law at the top of this file — and keeping a reason that nothing can
 * ever produce would advertise a behaviour the console does not have.
 */
export type InvalidationReason = "account_changed" | "disconnected";

export interface InvalidationState {
  reason: InvalidationReason;
  /** The signer the session belonged to, for the "you were signed in as…" line. */
  previousSigner: string | null;
  at: number;
}

type Listener = (state: InvalidationState | null) => void;

let invalidation: InvalidationState | null = null;
const listeners = new Set<Listener>();
let detach: (() => void) | null = null;

function emit(): void {
  for (const l of [...listeners]) l(invalidation);
}

/** Subscribe to invalidation state. Returns an unsubscribe function. */
export function subscribeInvalidation(l: Listener): () => void {
  listeners.add(l);
  l(invalidation);
  return () => {
    listeners.delete(l);
  };
}

export function getInvalidation(): InvalidationState | null {
  return invalidation;
}

/**
 * Clear the latch.
 *
 * Called ONLY by a completed login. Nothing else may clear it — not a page
 * reload, not switching back to the original account, not a successful API call.
 * That asymmetry is the property: re-authentication has to be explicit.
 */
export function clearInvalidation(): void {
  if (invalidation === null) return;
  invalidation = null;
  emit();
}

/**
 * How the session is ended server-side.
 *
 * Injected rather than imported so this module does not depend on the API
 * client, which imports config, which throws at module load on a non-test host.
 * Tests supply a spy; the app supplies the real POST.
 *
 * It receives the bearer EXPLICITLY. That is not a convenience — the local
 * session is torn down before the revoke is attempted (see `invalidate`), so by
 * the time this runs the store is already empty and a handler that read the
 * token itself would send an unauthenticated POST, get a 401, and leave the
 * session live on the server while the console reported it ended. §15 requires
 * the revoke to actually happen, so the token is captured first and handed in.
 */
export type LogoutFn = (bearer: string | null) => Promise<void>;

let logout: LogoutFn = async () => {};

export function setLogoutHandler(fn: LogoutFn): void {
  logout = fn;
}

/**
 * End the browser session and latch the reason.
 *
 * The local session is cleared FIRST and unconditionally. If the server call
 * fails — offline, 503, anything — the console must still stop presenting an
 * authority it no longer believes in; a network error is not a reason to keep
 * showing a signed-in header. The server-side revoke is attempted after, and its
 * failure is swallowed deliberately: the session will expire on its own, and
 * there is nothing useful for the operator to do about it here.
 */
export async function invalidate(reason: InvalidationReason): Promise<void> {
  const previous = getSessionMetadata();
  // Captured BEFORE the teardown below, because the revoke needs it and
  // `clearSession()` is about to make it unreachable.
  const bearer = getSessionToken();
  // Idempotent: a provider that fires more than one event at once must not
  // produce two invalidations, and must not overwrite the first reason.
  if (invalidation !== null) return;

  invalidation = {
    reason,
    previousSigner: previous?.walletAddress ?? null,
    at: Date.now(),
  };
  clearSession();
  emit();

  try {
    await logout(bearer);
  } catch {
    /* the local session is already gone; the server row expires on its own */
  }
}

/** The minimal provider surface this module needs. */
export interface EventfulProvider {
  on?: (event: string, handler: (...args: unknown[]) => void) => void;
  removeListener?: (event: string, handler: (...args: unknown[]) => void) => void;
}

function normalize(address: unknown): string | null {
  return typeof address === "string" && address !== "" ? address.toLowerCase() : null;
}

/**
 * Parse a `chainChanged` payload.
 *
 * MetaMask sends a hex string; some wallets send a number. Anything else parses
 * to null, which the UI renders as "unknown" — it is a display value now, not a
 * verdict about authority.
 */
export function parseChainId(raw: unknown): number | null {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string") {
    const n = raw.startsWith("0x") ? Number.parseInt(raw, 16) : Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Start listening. Idempotent — calling twice does not double-subscribe.
 *
 * `sessionSigner` is read lazily on every event rather than captured, because a
 * login can complete after the listeners are attached and the closure would
 * otherwise compare against a stale value.
 */
// ─── The connected chain, as presentation ─────────────────────────────────
//
// Tracked so the console can say which network the wallet is on — the login
// screen names a network canonical SIWE would refuse, and every signing flow
// binds the network the wallet reports — and because a row that silently goes
// stale is worse than no row.
//
// It is not identity. Nothing here reads the session, ends it, or changes what
// the console believes about the operator, the signer or the vault.

let connectedChainId: number | null = null;
const chainListeners = new Set<(id: number | null) => void>();

/** The chain the wallet last reported. Null until an event or a read. */
export function getConnectedChainId(): number | null {
  return connectedChainId;
}

/** Record the chain observed at connect time, before any event fires. */
export function setConnectedChainId(id: number | null): void {
  if (connectedChainId === id) return;
  connectedChainId = id;
  for (const l of [...chainListeners]) l(id);
}

export function subscribeConnectedChain(l: (id: number | null) => void): () => void {
  chainListeners.add(l);
  l(connectedChainId);
  return () => {
    chainListeners.delete(l);
  };
}

export function attachProviderEvents(provider: EventfulProvider | null): () => void {
  if (provider?.on === undefined) return () => {};
  if (detach !== null) return detach;

  const onAccounts = (...args: unknown[]) => {
    const accounts = Array.isArray(args[0]) ? (args[0] as unknown[]) : [];
    const active = normalize(accounts[0]);
    const session = getSessionMetadata();
    if (session === null) return; // nothing to protect

    if (active === null) {
      // The wallet disconnected or locked. The session is not necessarily
      // compromised, but the console can no longer demonstrate control of the
      // signer, and continuing to present its authority would be a claim we
      // cannot support.
      void invalidate("disconnected");
      return;
    }
    if (active !== session.walletAddress.toLowerCase()) {
      void invalidate("account_changed");
    }
    // Same account: nothing happens. A provider re-emitting the current account
    // must not end a healthy session.
  };

  const onChain = (...args: unknown[]) => {
    // PRESENTATION ONLY, and the absence of everything else here is the point.
    // This handler does not read the session, does not end it, and does not
    // touch operator, signer or authority. The canonical authority is server-side
    // on chain 84532 and the server re-reads membership there regardless of what
    // network this wallet is pointed at, so which chain MetaMask is showing has
    // no bearing on who is signed in.
    setConnectedChainId(parseChainId(args[0]));
  };

  provider.on("accountsChanged", onAccounts);
  provider.on("chainChanged", onChain);

  detach = () => {
    provider.removeListener?.("accountsChanged", onAccounts);
    provider.removeListener?.("chainChanged", onChain);
    detach = null;
  };
  return detach;
}

/** Test seam. Never called by the app. */
export function __resetProviderEventsForTest(): void {
  invalidation = null;
  listeners.clear();
  detach?.();
  detach = null;
  logout = async () => {};
}
